import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import LegacyButton from '../LegacyButton';
import Typography from '../Typography';
import InputGroup from '../InputGroup';
import { Icons } from '@ohif/ui-next';

const StudyListFilter = ({
  filtersMeta,
  filterValues,
  onChange,
  clearFilters,
  isFiltering,
  numOfStudies,
  onUploadClick,
  getDataSourceConfigurationComponent,
}) => {
  const { t } = useTranslation('StudyList');
  const { sortBy, sortDirection } = filterValues;
  const filterSorting = { sortBy, sortDirection };
  const setFilterSorting = sortingValues => {
    onChange({
      ...filterValues,
      ...sortingValues,
    });
  };
  const isSortingEnabled = numOfStudies > 0 && numOfStudies <= 100;
  //the clear filter div
  return (
    <React.Fragment>
      <div>
        <div style={{ backgroundColor: '#454343' }}>
          <div
            style={{
              position: 'relative',
              maxWidth: '100%',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              paddingTop: '1.25rem',
            }}
          >
            <div
              style={{
                marginBottom: '1.25rem',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {/* Left Side: Upload + Data Source */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '1.5rem',
                  flexShrink: 1,
                  minWidth: '1px',
                }}
              >
                {getDataSourceConfigurationComponent && getDataSourceConfigurationComponent()}

                {onUploadClick && (
                  <div
                    style={{
                      color: '#007bff',
                      display: 'flex',
                      cursor: 'pointer',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                    }}
                    onClick={onUploadClick}
                  >
                    <Icons.Upload />
                    <span>{t('Upload')}</span>
                  </div>
                )}
              </div>

              {/* Right Side: Clear Filters + Filter Inputs */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '34px',
                  flex: 1,
                  justifyContent: 'flex-end',
                  gap: '1rem',
                }}
              >
                {isFiltering && (
                  <LegacyButton
                    startIcon={<Icons.Cancel />}
                    onClick={clearFilters}
                    style={{
                      marginLeft: '1rem',
                      marginRight: '1rem',
                      borderRadius: '10px', // full rounded
                      border: '1px solid rgb(200, 46, 26)', // yellow border
                      backgroundColor: 'rgba(92, 65, 65, 0.2)', // 80% transparent background
                      color: '#cbd1d1',
                      padding: '0.1rem 1rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    {t('ClearFilters')}
                  </LegacyButton>
                )}

                <div
                  style={{
                    backgroundColor: '#141414', //the search bars
                    padding: '0.75rem',
                    flex: 1,
                  }}
                >
                  <InputGroup
                    inputMeta={filtersMeta}
                    values={filterValues}
                    onValuesChange={onChange}
                    sorting={filterSorting}
                    onSortingChange={setFilterSorting}
                    isSortingEnabled={isSortingEnabled}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          top: '-0.25rem',
          zIndex: 10,
          marginLeft: 'auto',
          marginRight: 'auto',
          borderBottomWidth: '4px',
          borderBottomColor: 'black',
          maxWidth: '100%',
          backgroundColor: '#13871f', // bg-primary-dark
        }}
      >
        {numOfStudies > 100 && (
          <div style={{ maxWidth: '100%', margin: '0 auto' }}>
            <div
              style={{
                backgroundColor: '#007bff', // bg-primary-main
                borderBottomLeftRadius: '0.5rem',
                borderBottomRightRadius: '0.5rem',
                paddingTop: '0.25rem',
                paddingBottom: '0.25rem',
                textAlign: 'center',
                fontSize: '1rem',
              }}
            >
              <p style={{ color: 'white' }}>
                {t('Filter list to 100 studies or less to enable sorting')}
              </p>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

StudyListFilter.propTypes = {
  filtersMeta: PropTypes.arrayOf(
    PropTypes.shape({
      /** Identifier used to map a field to it's value in `filterValues` */
      name: PropTypes.string.isRequired,
      /** Friendly label for filter field */
      displayName: PropTypes.string.isRequired,
      /** One of the supported filter field input types */
      inputType: PropTypes.oneOf(['Text', 'MultiSelect', 'DateRange', 'None']).isRequired,
      isSortable: PropTypes.bool.isRequired,
      /** Size of filter field in a 12-grid system */
      gridCol: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).isRequired,
      /** Options for a "MultiSelect" inputType */
      option: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  filterValues: PropTypes.object.isRequired,
  numOfStudies: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  isFiltering: PropTypes.bool.isRequired,
  onUploadClick: PropTypes.func,
  getDataSourceConfigurationComponent: PropTypes.func,
};

export default StudyListFilter;
